/**
 * 이슈 게시판(`/community/issues`) 공용 상수·타입 (JCG-3029).
 *
 * 클라이언트/서버 양쪽에서 import 하는 순수 모듈이다. Supabase·Clerk 등 서버 전용
 * 의존성을 두지 않는다. DB CHECK 제약(`supabase/migrations/20260917090000_community_issues_board.sql`)과
 * 카테고리·상태 집합·길이 제한을 반드시 일치시킨다.
 */

export const COMMUNITY_ISSUES_PATH = "/community/issues";

export const COMMUNITY_ISSUE_CATEGORIES = [
  "bug",
  "summary_pdf",
  "question",
  "suggestion",
] as const;
export type CommunityIssueCategory = (typeof COMMUNITY_ISSUE_CATEGORIES)[number];

export const COMMUNITY_ISSUE_STATUSES = [
  "open",
  "in_progress",
  "resolved",
  "closed",
] as const;
export type CommunityIssueStatus = (typeof COMMUNITY_ISSUE_STATUSES)[number];

/** 목록 필터: 열린 이슈(open·in_progress) / 닫힌 이슈(resolved·closed) / 전체 */
export type CommunityIssueStateFilter = "open" | "closed" | "all";

export const OPEN_COMMUNITY_ISSUE_STATUSES: readonly CommunityIssueStatus[] = [
  "open",
  "in_progress",
];
export const CLOSED_COMMUNITY_ISSUE_STATUSES: readonly CommunityIssueStatus[] = [
  "resolved",
  "closed",
];

export const COMMUNITY_ISSUE_CATEGORY_META: Record<
  CommunityIssueCategory,
  { label: string; emoji: string; description: string }
> = {
  bug: {
    label: "버그 제보",
    emoji: "🐛",
    description: "화면 오류, 채점 오류, 동작하지 않는 기능을 알려주세요.",
  },
  summary_pdf: {
    label: "요약본",
    emoji: "📄",
    description: "요약 PDF의 오탈자·그림 오류·내용 질문을 남겨주세요.",
  },
  question: {
    label: "질문",
    emoji: "❓",
    description: "특정 페이지와 무관한 공부 방법·이용권·서비스 질문입니다.",
  },
  suggestion: {
    label: "건의",
    emoji: "💡",
    description: "있었으면 하는 기능이나 개선 아이디어를 제안해주세요.",
  },
};

/** 종류별 작성 힌트 — 작성 폼 안내문과 우측 "작성 가이드" 카드가 공유한다. */
export const COMMUNITY_ISSUE_CATEGORY_HINTS: Record<CommunityIssueCategory, string> = {
  bug: "어느 화면(주소)에서, 무엇을 했을 때, 어떻게 잘못 동작했는지 적어주세요. 스크린샷이 있으면 더 빨리 확인할 수 있어요.",
  summary_pdf:
    "요약본 버전(v표기)과 페이지, 잘못된 부분과 올바른 내용을 적어주세요. 수정되면 버전 변경 이력에 반영돼요.",
  question:
    "공부 방법, 이용권, 서비스 이용에 대한 질문을 남겨주세요. 특정 문제 질문은 문제 포럼이 더 빨라요.",
  suggestion: "어떤 상황에서 불편했고, 어떻게 바뀌면 좋을지 적어주시면 검토에 도움이 돼요.",
};

export const COMMUNITY_ISSUE_STATUS_META: Record<
  CommunityIssueStatus,
  { label: string; description: string }
> = {
  open: { label: "열림", description: "운영자 확인 대기 중" },
  in_progress: { label: "처리중", description: "운영자가 확인하고 처리하고 있어요" },
  resolved: { label: "완료", description: "처리가 끝났어요" },
  closed: { label: "닫힘", description: "처리하지 않고 닫았어요 (중복·범위 밖 등)" },
};

export const COMMUNITY_ISSUE_LIMITS = {
  title: 120,
  content: 5000,
  note: 1000,
  images: 3,
} as const;

export const COMMUNITY_ISSUES_PAGE_SIZE = 20;

/** 목록 검색(JCG-3859) 검색어 최대 길이와 단어 수. 단어마다 조건이 하나씩 붙으므로 쿼리 크기를 묶어 둔다. */
export const COMMUNITY_ISSUE_SEARCH_LIMITS = {
  queryLength: 100,
  terms: 5,
} as const;

/**
 * `?q=` 값을 검색어로 정리한다 — 문자열이 아니면 빈 값, 연속 공백은 하나로, 최대 길이로 자른다.
 * 길이는 코드 포인트 단위로 잘라 이모지 같은 서로게이트 쌍이 반쪽만 남지 않게 한다(반쪽은 RPC JSON 파싱 오류).
 */
export function normalizeCommunityIssueSearchQuery(value: unknown): string {
  if (typeof value !== "string") return "";
  const collapsed = value.replace(/\s+/g, " ").trim();
  return Array.from(collapsed).slice(0, COMMUNITY_ISSUE_SEARCH_LIMITS.queryLength).join("").trim();
}

/** 검색어를 공백 기준 단어로 나눈다(중복 제거, 최대 `terms` 개). 목록은 모든 단어가 들어간 글만 남긴다. */
export function splitCommunityIssueSearchTerms(query: string): string[] {
  const terms = normalizeCommunityIssueSearchQuery(query).split(" ").filter(Boolean);
  return [...new Set(terms)].slice(0, COMMUNITY_ISSUE_SEARCH_LIMITS.terms);
}

export function isCommunityIssueCategory(
  value: unknown
): value is CommunityIssueCategory {
  return (
    typeof value === "string" &&
    (COMMUNITY_ISSUE_CATEGORIES as readonly string[]).includes(value)
  );
}

export function isCommunityIssueStatus(
  value: unknown
): value is CommunityIssueStatus {
  return (
    typeof value === "string" &&
    (COMMUNITY_ISSUE_STATUSES as readonly string[]).includes(value)
  );
}

export function isCommunityIssueStateFilter(
  value: unknown
): value is CommunityIssueStateFilter {
  return value === "open" || value === "closed" || value === "all";
}

export function isOpenCommunityIssueStatus(status: CommunityIssueStatus): boolean {
  return OPEN_COMMUNITY_ISSUE_STATUSES.includes(status);
}

/**
 * 상태는 그대로 두고 메모만 남긴 타임라인 이벤트인지 — `setCommunityIssueStatus` 가 같은 상태에 메모를 받으면
 * `from_status` 를 null 로 기록한다. 타임라인 문구와 인앱 알림 문구(JCG-3615)가 이 규칙을 공유한다.
 */
export function isNoteOnlyIssueEvent(event: {
  fromStatus: CommunityIssueStatus | null | undefined;
}): boolean {
  return event.fromStatus == null;
}

/**
 * 외부 입력(API 본문)의 이슈 id 를 정수로 해석한다. `9`·`"9"` 만 통과시키고 앞자리 0·소수·음수·
 * 안전 정수 초과는 null — `Number("007")` 같은 정규화로 다른 글을 가리키지 않게 한다.
 */
export function parseCommunityIssueId(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!/^[1-9]\d{0,15}$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

/**
 * 이슈 댓글 스레드용 `comments.slug`. `getCommentPagePath(slug)` 가 `/${slug}` 로
 * 변환하므로 실제 라우트(`/community/issues/<id>`)와 동일한 형태를 쓴다.
 */
export function communityIssueCommentSlug(issueId: number): string {
  return `community/issues/${issueId}`;
}

/** 상세 페이지 `generateMetadata` 의 "use cache" 태그. 제목·내용 수정/삭제 시 무효화한다. */
export function communityIssueCacheTag(issueId: number): string {
  return `community-issue-${issueId}`;
}

export function communityIssueHref(issueId: number): string {
  return `${COMMUNITY_ISSUES_PATH}/${issueId}`;
}

export function newCommunityIssueHref(params?: {
  category?: CommunityIssueCategory;
  productId?: string;
}): string {
  const search = new URLSearchParams();
  if (params?.category) search.set("category", params.category);
  if (params?.productId) search.set("product", params.productId);
  const query = search.toString();
  return `${COMMUNITY_ISSUES_PATH}/new${query ? `?${query}` : ""}`;
}

export function communityIssuesListHref(params?: {
  state?: CommunityIssueStateFilter;
  category?: CommunityIssueCategory | null;
  /** 검색어(JCG-3859). 상태·종류를 바꿔도 검색을 유지하려면 현재 값을 그대로 넘긴다. */
  q?: string | null;
  page?: number;
}): string {
  const search = new URLSearchParams();
  if (params?.state && params.state !== "all") search.set("state", params.state);
  if (params?.category) search.set("category", params.category);
  const q = normalizeCommunityIssueSearchQuery(params?.q);
  if (q) search.set("q", q);
  if (params?.page && params.page > 1) search.set("page", String(params.page));
  const query = search.toString();
  return `${COMMUNITY_ISSUES_PATH}${query ? `?${query}` : ""}`;
}

export interface CommunityIssue {
  id: number;
  clerkUserId: string;
  author: string | null;
  profileImageUrl: string | null;
  passType: string | null;
  category: CommunityIssueCategory;
  status: CommunityIssueStatus;
  title: string;
  content: string;
  imageUrls: string[];
  productId: string | null;
  linearIssueId: string | null;
  statusChangedAt: string;
  resolvedAt: string | null;
  /** 제목·내용을 고친 마지막 시각. 없으면 미수정. */
  contentEditedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** 목록 조회 시에만 채워지는 댓글 수(삭제 댓글 제외). */
  commentCount?: number;
}

export interface CommunityIssueEvent {
  id: number;
  issueId: number;
  actorName: string | null;
  actorIsAdmin: boolean;
  fromStatus: CommunityIssueStatus | null;
  toStatus: CommunityIssueStatus;
  note: string | null;
  createdAt: string;
}
